import { useEffect } from "react";
import { ApiError } from "@/api/fetchApi";
import type { FieldEdit } from "@/generated/core/v1/questionnaire";
import { type FieldError, FieldError_Code } from "@/generated/core/v1/value_source";
import { fieldEdit, paths } from "@/plan-logic/questionnaire/build";

/**
 * A save the engine refused because an FHSA belongs to someone the data says isn't a first-time
 * home buyer, and the household hasn't confirmed moving it to the RRSP
 * (`FieldError.CODE_FHSA_TRANSFER_UNCONFIRMED`, path `persons[<uuid>].fhsa_accounts[<uuid>]`).
 * Every save path (onboarding, `useFamilySave`) answers it with this dialog: mark the holder a
 * first-time home buyer, confirm the move, or go back and fix the data.
 */
export type FhsaChoice = "first-time" | "move";

const FHSA_PATH = /^persons\[([^\]]+)\]\.fhsa_accounts\[([^\]]+)\]$/;

/** The refused FHSAs, when they are the save's only errors (else null: show the errors as usual). */
export function fhsaTransferErrors(e: unknown): FieldError[] | null {
  if (!(e instanceof ApiError) || !e.isValidation || !e.fieldErrors.length) return null;
  const ok = e.fieldErrors.every(
    (f) => f.code === FieldError_Code.FHSA_TRANSFER_UNCONFIRMED && FHSA_PATH.test(f.path),
  );
  return ok ? e.fieldErrors : null;
}

function holders(errors: FieldError[]): { person: string; fhsa: string }[] {
  return errors.flatMap((f) => {
    const m = FHSA_PATH.exec(f.path);
    return m ? [{ person: m[1], fhsa: m[2] }] : [];
  });
}

/** The field edits that settle the refused FHSAs, added to the same questionnaire and resent. */
export function fhsaChoiceEdits(errors: FieldError[], choice: FhsaChoice): FieldEdit[] {
  const list = holders(errors);
  if (choice === "first-time") {
    return [...new Set(list.map((h) => h.person))].map((p) =>
      fieldEdit(paths.personField(p, "is_first_time_home_buyer"), true),
    );
  }
  return list.map((h) => fieldEdit(paths.fhsaConfirmed(h.person, h.fhsa), true));
}

/** "you", "Sam", or "Sam and Alex", for the holders of the refused FHSAs. */
export function fhsaHolderNames(
  errors: FieldError[],
  nameOf: (personUuid: string) => string | undefined,
): { who: string; you: boolean; many: boolean } {
  const people = [...new Set(holders(errors).map((h) => h.person))];
  const names = people.map((p) => nameOf(p) || "you");
  const you = names.length === 1 && names[0] === "you";
  return { who: names.join(" and "), you, many: names.length > 1 };
}

export function FhsaTransferDialog({
  errors,
  nameOf,
  pending,
  error,
  onChoose,
  onFix,
  onClose,
}: {
  errors: FieldError[];
  nameOf: (personUuid: string) => string | undefined;
  pending: boolean;
  error?: string;
  onChoose: (choice: FhsaChoice) => void;
  /** Close, and take the user to the first-time home buyer boxes under "What you owe". */
  onFix: () => void;
  /** Close and save nothing. */
  onClose: () => void;
}) {
  const { who, you, many } = fhsaHolderNames(errors, nameOf);
  const fhsas = holders(errors).length > 1 ? "FHSAs" : "FHSA";
  const isnt = you || many ? "aren’t" : "isn’t";
  const whose = you ? "your" : many ? "their" : `${who}’s`;
  const buyer = many ? "first-time home buyers" : "a first-time home buyer";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !pending && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, pending]);

  return (
    <div
      className="ciwrap fhsadlg"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fhsa-title"
      data-testid="fhsa-dialog"
    >
      <div className="cimodal">
        <div className="cihead">
          <div>
            <div className="eyebrow">Before we save</div>
            <h2 id="fhsa-title" style={{ fontSize: 20, marginTop: 3 }}>
              {you ? "Are you" : many ? `Are ${who}` : `Is ${who}`} {buyer}?
            </h2>
          </div>
          <button
            type="button"
            className="cix"
            onClick={onClose}
            aria-label="Close"
            disabled={pending}
          >
            &times;
          </button>
        </div>
        <p className="mut" style={{ margin: "0 0 16px", maxWidth: "60ch" }}>
          Your data says {you ? "you" : who} {isnt} {buyer}, but{" "}
          {you ? "you have" : many ? "they have" : `${who} has`}{" "}
          {fhsas === "FHSA" ? "an FHSA" : "FHSAs"}. Only first-time home buyers can keep saving in
          an FHSA. If that&rsquo;s right, the balance should move to {whose} RRSP, tax-free.
        </p>
        <div className="choices">
          <button
            type="button"
            className="choice"
            onClick={() => onChoose("first-time")}
            disabled={pending}
            data-testid="fhsa-first-time"
          >
            <span className="ic">🏠</span>
            <span>
              <b>
                {you ? "I’m" : many ? "They’re" : `${who} is`} {buyer}
              </b>
              <p>
                Abi plans with the {fhsas} for a home purchase, or moves{" "}
                {fhsas === "FHSA" ? "it" : "them"} to {whose} RRSP if you have no home-buying goal.
              </p>
            </span>
          </button>
          <button
            type="button"
            className="choice"
            onClick={() => onChoose("move")}
            disabled={pending}
            data-testid="fhsa-move"
          >
            <span className="ic">↪</span>
            <span>
              <b>Move the {fhsas} to the RRSP</b>
              <p>Abi adds the move to your plan as an action, and won&rsquo;t ask again.</p>
            </span>
          </button>
          <button
            type="button"
            className="choice"
            onClick={onFix}
            disabled={pending}
            data-testid="fhsa-fix"
          >
            <span className="ic">✎</span>
            <span>
              <b>Let me fix my data</b>
              <p>
                Go back to &ldquo;What you owe&rdquo;, where you say who&rsquo;s a first-time home
                buyer, and change it yourself. Nothing is saved yet.
              </p>
            </span>
          </button>
        </div>
        {pending && (
          <p className="mut" style={{ marginTop: 12 }}>
            Saving…
          </p>
        )}
        {error && (
          <p className="wp-form-err" role="alert" style={{ marginTop: 12 }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
